import React, { ReactNode, useState } from 'react'
import { useKey } from 'react-use'
import Modal from 'components/Modal'
import Button, { Variant } from 'components/Button'
import { keyWithoutModifier } from 'utils/keyboardEvents'
import { ModalInjectedProps } from 'containers/ModalManager'

interface ButtonProps {
  text?: string
  variant?: Variant
  loading?: boolean
  disabled?: boolean
}

interface ConfirmationDialogProps extends ModalInjectedProps {
  title: ReactNode
  cancelButton?: ButtonProps
  onHide?: () => void
  confirmButton: ButtonProps
  onConfirm: () => void
  children: React.ReactNode
}

const ConfirmationDialog = ({
  title,
  hideModal,
  cancelButton: cancelButtonProp,
  onHide,
  confirmButton: confirmButtonProp,
  onConfirm,
  children,
}: ConfirmationDialogProps) => {
  const [isTabPressed, setIsTabPressed] = useState(false)

  const cancelButton = { text: 'Cancel', disabled: false, ...cancelButtonProp }
  const confirmButton = { variant: 'negative' as Variant, ...confirmButtonProp }

  const handleCancel = () => {
    onHide?.()
    hideModal()
  }

  const handleConfirmOnEnterPressed = () => {
    // In order to mantain "Cancel" and "Confirm" buttons accessible when
    // navigating with "tab", it's only possible to confirm when "Enter" is
    // pressed before using "tab" navigation.
    // The usage of "tab" will disable this functionality.
    if (!isTabPressed) onConfirm()
  }

  const handleTabPressed = () => {
    setIsTabPressed(true)
  }

  useKey(keyWithoutModifier('Enter'), handleConfirmOnEnterPressed, {
    event: 'keyup',
  })
  useKey(keyWithoutModifier('Tab'), handleTabPressed, { event: 'keyup' })

  return (
    <Modal title={title} onCancel={handleCancel}>
      <Modal.Body>{children}</Modal.Body>
      <Modal.Footer>
        <Button disabled={cancelButton.disabled} onClick={handleCancel}>
          {cancelButton.text}
        </Button>
        <Button
          data-testid="confirmation-button"
          variant={confirmButton.variant}
          loading={confirmButton.loading}
          disabled={confirmButton.disabled}
          onClick={onConfirm}>
          {confirmButton.text}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ConfirmationDialog
